import {
  ColDef,
  ColGroupDef,
  Column,
  ColumnApi,
} from "ag-grid-community";

/**
 * LPP-20500 (perf) — larghezze FISSE per tipo di colonna del Report Disponibilità.
 *
 * Prima le larghezze erano calcolate a runtime con `autoSizeAllColumns()` (misura header+celle di OGNI colonna
 * di OGNI griglia: con 180 giorni sono migliaia di colonne e il costo dominava il rendering, ~50% del tempo CPU).
 * I valori sotto sono quelli che l'auto-size produceva (misurati per lingua, header + contenuto celle): le colonne
 * dello stesso tipo hanno sempre la stessa larghezza, quindi il risultato visivo è invariato.
 *
 * La chiave è `<colore header>:<minWidth originale>` e identifica univocamente il tipo di colonna:
 *   black  = giorni passati ("fine giornata")        yellow = oggi inizio giornata
 *   green  = oggi tempo reale                        none   = giorni futuri
 */
type Widths = Record<string, number>;

/** colId della colonna pinned (nomi gruppo/drill-down): derivato da `field: "group"`. */
export const GROUP_COL_ID = "group";

export const COLUMN_WIDTHS: Record<string, Widths> = {
  it: {
    "black:290": 172, "black:50": 80, "black:175": 117, "black:190": 80,
    "yellow:270": 149, "yellow:90": 80, "yellow:190": 117, "yellow:100": 80,
    "green:270": 172, "green:90": 80, "green:190": 117, "green:100": 86,
    "none:175": 135, "none:190": 149, "none:120": 91,
  },
  en: {
    "black:290": 152, "black:50": 80, "black:175": 140, "black:190": 80,
    "yellow:270": 216, "yellow:90": 80, "yellow:190": 140, "yellow:100": 80,
    "green:270": 152, "green:90": 80, "green:190": 140, "green:100": 86,
    "none:175": 141, "none:190": 216, "none:120": 91,
  },
  fr: {
    "black:290": 187, "black:50": 80, "black:175": 127, "black:190": 80,
    "yellow:270": 161, "yellow:90": 80, "yellow:190": 127, "yellow:100": 80,
    "green:270": 187, "green:90": 80, "green:190": 127, "green:100": 86,
    "none:175": 150, "none:190": 165, "none:120": 91,
  },
  es: {
    "black:290": 191, "black:50": 80, "black:175": 172, "black:190": 80,
    "yellow:270": 256, "yellow:90": 80, "yellow:190": 172, "yellow:100": 80,
    "green:270": 191, "green:90": 80, "green:190": 172, "green:100": 86,
    "none:175": 145, "none:190": 256, "none:120": 91,
  },
  pt: {
    "black:290": 190, "black:50": 80, "black:175": 168, "black:190": 80,
    "yellow:270": 240, "yellow:90": 80, "yellow:190": 168, "yellow:100": 80,
    "green:270": 190, "green:90": 80, "green:190": 168, "green:100": 86,
    "none:175": 146, "none:190": 240, "none:120": 91,
  },
  fi: {
    "black:290": 146, "black:50": 80, "black:175": 156, "black:190": 80,
    "yellow:270": 204, "yellow:90": 80, "yellow:190": 156, "yellow:100": 80,
    "green:270": 146, "green:90": 80, "green:190": 156, "green:100": 86,
    "none:175": 135, "none:190": 204, "none:120": 91,
  },
};

const colorOf = (headerClass: ColDef["headerClass"]): string => {
  const hc = typeof headerClass === "string" ? headerClass : "";
  if (hc.includes("black")) return "black";
  if (hc.includes("yellow")) return "yellow";
  if (hc.includes("green")) return "green";
  return "none";
};

/**
 * Applica a tutte le colonne foglia "dati" una larghezza fissa (per lingua) e le esclude da sizeColumnsToFit.
 * La colonna pinned (nomi gruppo/drill-down) resta invariata: la sua larghezza segue il contenuto con un
 * auto-size limitato a quella sola colonna (vedi `autoSizeGroupColumn` nelle tabelle).
 */
export const applyFixedColumnWidths = (
  cols: (ColDef | ColGroupDef)[],
  language: string
): (ColDef | ColGroupDef)[] => {
  const widths = COLUMN_WIDTHS[(language || "it").slice(0, 2).toLowerCase()] ?? COLUMN_WIDTHS.en;
  const walk = (list: (ColDef | ColGroupDef)[]): (ColDef | ColGroupDef)[] =>
    list.map((col) => {
      if ("children" in col && Array.isArray(col.children)) {
        return { ...col, children: walk(col.children as (ColDef | ColGroupDef)[]) };
      }
      const leaf = col as ColDef;
      if (leaf.pinned) return { ...leaf, suppressSizeToFit: true };
      const kind = `${colorOf(leaf.headerClass)}:${leaf.minWidth}`;
      const width = widths[kind] ?? leaf.minWidth ?? 80;
      return {
        ...leaf,
        width,
        minWidth: Math.min(width, 80),
        suppressSizeToFit: true,
        // tipo di colonna: usato da `fitVisibleColumnTypes`.
        refData: { [WIDTH_KIND]: kind },
      };
    });
  return walk(cols);
};

const WIDTH_KIND = "widthKind";

const kindOf = (col: Column): string | undefined =>
  col.getColDef().refData?.[WIDTH_KIND];

/** Stato del raffinamento delle larghezze di UNA griglia (colonne già misurate e larghezza raggiunta per tipo). */
export type FitState = {
  cols: Set<string>;
  kindWidth: Map<string, number>;
};
export const newFitState = (): FitState => ({
  cols: new Set(),
  kindWidth: new Map(),
});
export const resetFitState = (state: FitState): void => {
  state.cols.clear();
  state.kindWidth.clear();
};

/**
 * LPP-20500 (perf) — raffinamento delle larghezze al contenuto REALE (etichette tradotte e valori dei dati).
 * Le larghezze fisse sono solo il punto di partenza (prima della misura). Qui si misurano (autosize) le colonne
 * VISIBILI non ancora misurate; la larghezza di ogni tipo di colonna è il massimo misurato finora e può solo CRESCERE
 * man mano che entrano nel viewport colonne con contenuto più largo (nessun troncamento a regime). Il valore si applica
 * a tutte le colonne dello stesso tipo. Costo: poche decine di colonne misurate invece di centinaia.
 */
export const fitVisibleColumnTypes = (
  columnApi: ColumnApi,
  state: FitState
): void => {
  const fresh = columnApi
    .getAllDisplayedVirtualColumns()
    .filter((col) => kindOf(col) && !state.cols.has(col.getColId()));
  if (fresh.length === 0) return;

  columnApi.autoSizeColumns(fresh);

  const touched = new Set<string>();
  fresh.forEach((col) => {
    const kind = kindOf(col) as string;
    const current = state.kindWidth.get(kind) ?? 0;
    state.kindWidth.set(kind, Math.max(current, col.getActualWidth()));
    touched.add(kind);
    state.cols.add(col.getColId());
  });

  const widths: { key: Column; newWidth: number }[] = [];
  columnApi.getAllGridColumns().forEach((col) => {
    const kind = kindOf(col);
    if (!kind || !touched.has(kind)) return;
    const target = state.kindWidth.get(kind) as number;
    if (col.getActualWidth() !== target) widths.push({ key: col, newWidth: target });
  });
  if (widths.length > 0) columnApi.setColumnWidths(widths);
};
